import Link from 'next/link';
import type { PortableTextComponents } from '@portabletext/react';

import { BodyImage } from './blocks/BodyImage';
import type { SanityBodyImage } from './blocks/BodyImage';
import { CtaBannerBlock } from './blocks/CtaBannerBlock';
import { EmbedBlock } from './blocks/EmbedBlock';
import { FixtureTickerBlock } from './blocks/FixtureTickerBlock';
import { HeadToHeadBlock } from './blocks/HeadToHeadBlock';
import { PlayerFixturesBlock } from './blocks/PlayerFixturesBlock';
import { PlayerListBlock } from './blocks/PlayerListBlock';
import { PlayerProfileBlock } from './blocks/PlayerProfileBlock';
import { StatCalloutBlock } from './blocks/StatCalloutBlock';
import { ARTICLE_TOKENS, SCROLL_MARGIN_CLASS } from './design-tokens';
import { headingIdFor } from '@lib/article-blocks/headings';
import type { ArticleHeading } from '@lib/article-blocks/headings';
import { EMPTY_ARTICLE_BLOCK_DATA } from '@lib/article-blocks/hydrate';
import type {
  ArticleBlockData,
  SanityCtaBannerBlock,
  SanityEmbedBlock,
  SanityFixtureTickerBlock,
  SanityHeadToHeadBlock,
  SanityPlayerFixturesBlock,
  SanityPlayerLinkMark,
  SanityPlayerListBlock,
  SanityPlayerProfileBlock,
  SanityStatCalloutBlock
} from '@lib/article-blocks/types';

// The legacy WordPress stylesheet (`.article p a` in scss/_article.scss) still
// reaches these marks: it recolours them and draws a border-bottom underline,
// which stacked with `hover:underline` into a double line. The important
// modifiers let the marks own their colour and use one text underline instead.
// Colours mirror ARTICLE_TOKENS.ink and ARTICLE_TOKENS.accentText; Tailwind
// only generates classes it can read literally, so they cannot be composed.
const INLINE_LINK_CLASS_NAME =
  '!border-b-0 !text-[#14171c] underline decoration-1 decoration-[#1f7fc4] underline-offset-[3px] transition-colors hover:!text-[#1f7fc4]';

/**
 * Custom object blocks (player cards, fixture tickers, FFH-2712) register here
 * so every article route renders the same set. Block data is closed over
 * rather than read from context so the block components stay hook-free.
 */
export const buildArticlePortableTextComponents = (
  blockData: ArticleBlockData = EMPTY_ARTICLE_BLOCK_DATA,
  headings: ArticleHeading[] = []
): PortableTextComponents => ({
  block: {
    normal: ({ children }) => (
      <p
        className={`mb-5 font-body text-lg leading-[1.65] ${ARTICLE_TOKENS.body}`}
      >
        {children}
      </p>
    ),
    // The heavy rule above each H2 is what gives long list articles their
    // section rhythm; the id is what the table of contents links to.
    h2: ({ children, value }) => (
      <h2
        id={headingIdFor(
          headings,
          (value.children ?? [])
            .map((child) => String(child.text ?? ''))
            .join('')
            .trim()
        )}
        className={`mt-[52px] mb-2 border-t-2 border-[#14171c] pt-5 font-heading text-[27px] font-bold leading-[1.2] tracking-[-0.015em] ${ARTICLE_TOKENS.ink} ${SCROLL_MARGIN_CLASS} [text-wrap:pretty] [&+p]:mt-6`}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        className={`mt-9 mb-3 font-heading text-xl font-bold leading-[1.25] ${ARTICLE_TOKENS.ink}`}
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        className={`mt-7 mb-2 font-heading text-lg font-bold ${ARTICLE_TOKENS.ink}`}
      >
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className={`my-6 border-l-[3px] border-[#3fa9f5] pl-5 font-body text-lg italic ${ARTICLE_TOKENS.bodyMuted}`}
      >
        {children}
      </blockquote>
    )
  },
  list: {
    bullet: ({ children }) => (
      <ul
        className={`mb-5 list-disc pl-6 font-body text-lg leading-[1.65] ${ARTICLE_TOKENS.body}`}
      >
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol
        className={`mb-5 list-decimal pl-6 font-body text-lg leading-[1.65] ${ARTICLE_TOKENS.body}`}
      >
        {children}
      </ol>
    )
  },
  types: {
    playerFixturesBlock: ({ value }: { value: SanityPlayerFixturesBlock }) => (
      <PlayerFixturesBlock value={value} data={blockData} />
    ),
    playerProfileBlock: ({ value }: { value: SanityPlayerProfileBlock }) => (
      <PlayerProfileBlock value={value} data={blockData} />
    ),
    headToHeadBlock: ({ value }: { value: SanityHeadToHeadBlock }) => (
      <HeadToHeadBlock value={value} data={blockData} />
    ),
    fixtureTickerBlock: ({ value }: { value: SanityFixtureTickerBlock }) => (
      <FixtureTickerBlock value={value} data={blockData} />
    ),
    statCalloutBlock: ({ value }: { value: SanityStatCalloutBlock }) => (
      <StatCalloutBlock value={value} />
    ),
    playerListBlock: ({ value }: { value: SanityPlayerListBlock }) => (
      <PlayerListBlock value={value} data={blockData} />
    ),
    ctaBannerBlock: ({ value }: { value: SanityCtaBannerBlock }) => (
      <CtaBannerBlock value={value} />
    ),
    embedBlock: ({ value }: { value: SanityEmbedBlock }) => (
      <EmbedBlock value={value} />
    ),
    image: ({ value }: { value: SanityBodyImage }) => (
      <BodyImage value={value} />
    )
  },
  marks: {
    strong: ({ children }) => (
      <strong className={`font-semibold ${ARTICLE_TOKENS.ink}`}>
        {children}
      </strong>
    ),
    link: ({ value, children }) => {
      const isExternal = Boolean(value?.blank);
      return (
        <a
          href={value?.href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className={INLINE_LINK_CLASS_NAME}
        >
          {children}
        </a>
      );
    },
    // The path is resolved at build time from the player's optaUuid, so a
    // player the API no longer knows falls back to the plain name rather than
    // a link to a guess.
    playerLink: ({
      value,
      children
    }: {
      value?: SanityPlayerLinkMark;
      children?: React.ReactNode;
    }) => {
      const href = value?.optaUuid
        ? blockData.profilePathsByOptaUuid[value.optaUuid]
        : undefined;
      if (!href) return <>{children}</>;
      return (
        <Link
          href={href}
          className={INLINE_LINK_CLASS_NAME}
          data-cy="player-link"
        >
          {children}
        </Link>
      );
    }
  }
});
